/**
 * The shared stretch of a wall between two rooms, and clearing the openings on it.
 * Used by Island (FIELD-2 E15) and Remove wall / Open to (FIELD-3 J1, J6).
 */
import { EPS, findRoom } from './geom';
import { deleteOpening } from './openings';
import type { Plan, Room, SharedInterval, SharedMap } from './types';

export interface Across { r: Room; vid: number; q: Room; iv: SharedInterval; back: SharedInterval | undefined }

/** The shared stretch of wall `vid` facing `otherRid` (or the only room across), optionally the one containing `at`. */
export function sharedStretch(
  plan: Plan, M: SharedMap, rid: string, vid: number, otherRid?: string, at?: number,
): Across | null {
  const r = findRoom(plan, rid);
  const ivs = M.get(rid)?.get(vid) ?? [];
  const iv = ivs.find((v) => (otherRid == null || v.orid === otherRid) && (at == null || (at >= v.lo - EPS && at <= v.hi + EPS)));
  const q = iv && findRoom(plan, iv.orid);
  if (!r || !iv || !q) return null;
  const back = M.get(q.id)?.get(iv.ovid)?.find((v) => v.orid === rid && v.ovid === vid);
  return { r, vid, q, iv, back };
}

/** Remove every opening overlapping the shared stretch, on both sides (with linked copies). */
export function clearStretch(plan: Plan, a: Across): number {
  let n = 0;
  const sweep = (r: Room, vid: number, lo: number, hi: number) => {
    for (const o of [...r.openings]) {
      if (o.edgeStart !== vid || o.offFt >= hi - EPS || o.offFt + o.widthFt <= lo + EPS) continue;
      if (!r.openings.includes(o)) continue;
      const res = deleteOpening(plan, r.id, o.id);
      if (res.ok) n += res.partnerDeleted ? 2 : 1;
    }
  };
  sweep(a.r, a.vid, a.iv.lo, a.iv.hi);
  if (a.back) sweep(a.q, a.iv.ovid, a.back.lo, a.back.hi);
  return n;
}

/** The lower of the two rooms' ceilings (E14). */
export const lowerCeilingFt = (a: Across): number => Math.min(a.r.ceilingFt, a.q.ceilingFt);
